import { useState, useEffect, useRef } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { ClientLayout, AdminLayout, PageHeader } from '../components/Layout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import { 
  Download, 
  ArrowLeft, 
  Printer,
  FileText,
  Calendar,
  Building2,
  User,
  UserCog,
  Send,
  Copy,
  CheckCircle2,
  Layers,
  Plus,
  Trash2,
  Bell,
  Loader2,
  Clock,
  Edit,
  FileCheck,
  History,
  RefreshCw,
  Tag,
  ClipboardList,
  ShieldCheck,
  AlertTriangle,
  Sparkles,
  Search,
  TrendingUp,
  XCircle,
  Wrench
} from 'lucide-react';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;
const LOGO_URL = "https://kroytechnologygroup.com/wp-content/uploads/2022/01/KTG-Final-Logo-Flat-300x200.png";

export default function SubmissionPreview({ isAdmin = false }) {
  const { submissionId } = useParams();
  const navigate = useNavigate();
  const [submission, setSubmission] = useState(null);
  const [template, setTemplate] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sendingToVendor, setSendingToVendor] = useState(false);
  const [sendingToDepts, setSendingToDepts] = useState(false);
  const [vendorLinkCopied, setVendorLinkCopied] = useState(false);
  const [savingRenewal, setSavingRenewal] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [reopening, setReopening] = useState(false);
  const [auditLog, setAuditLog] = useState([]);
  const [auditExpanded, setAuditExpanded] = useState(false);
  const printRef = useRef(null);

  // Renewal state
  const [renewalDate, setRenewalDate] = useState('');
  const [renewalCycle, setRenewalCycle] = useState('');
  const [reminderRecipients, setReminderRecipients] = useState([]);

  useEffect(() => {
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [submissionId]);

  const loadData = async () => {
    try {
      const endpoint = isAdmin 
        ? `${API_URL}/admin/submissions/${submissionId}`
        : `${API_URL}/submissions/${submissionId}`;
      
      const submissionRes = await axios.get(endpoint);
      const sub = submissionRes.data;
      setSubmission(sub);
      
      const templateRes = await axios.get(`${API_URL}/templates/${sub.template_id}`);
      setTemplate(templateRes.data);

      setRenewalDate(sub.renewal_date || '');
      setRenewalCycle(sub.renewal_cycle || '');
      setReminderRecipients(sub.reminder_recipients || []);

      // Load audit log
      try {
        const auditEndpoint = isAdmin
          ? `${API_URL}/admin/submissions/${submissionId}/audit-log`
          : `${API_URL}/submissions/${submissionId}/audit-log`;
        const auditRes = await axios.get(auditEndpoint);
        setAuditLog(auditRes.data);
      } catch { /* audit log may be empty */ }
    } catch (error) {
      toast.error('Failed to load submission');
      navigate(isAdmin ? '/admin/submissions' : '/dashboard');
    } finally {
      setLoading(false);
    }
  };

  const handlePrint = () => window.print();

  const handleDownloadPDF = async () => {
    try {
      const endpoint = isAdmin 
        ? `${API_URL}/admin/submissions/${submissionId}/pdf`
        : `${API_URL}/submissions/${submissionId}/pdf`;
      
      const response = await axios.get(endpoint, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${submission.template_name.replace(/ /g, '_')}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('PDF downloaded!');
    } catch (error) {
      toast.error('Failed to download PDF');
    }
  };

  const handleSendToVendor = async () => {
    setSendingToVendor(true);
    try {
      const response = await axios.post(`${API_URL}/submissions/${submissionId}/send-to-vendor`);
      toast.success(response.data.message);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send vendor link');
    } finally {
      setSendingToVendor(false);
    }
  };

  const handleSendToDepts = async () => {
    setSendingToDepts(true);
    try {
      const response = await axios.post(`${API_URL}/submissions/${submissionId}/send-to-departments`);
      toast.success(response.data.message);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send department links');
    } finally {
      setSendingToDepts(false);
    }
  };

  const copyVendorLink = () => {
    if (submission?.vendor_token) {
      const link = `${window.location.origin}/vendor/${submission.vendor_token}`;
      navigator.clipboard.writeText(link);
      setVendorLinkCopied(true);
      toast.success('Vendor link copied to clipboard!');
      setTimeout(() => setVendorLinkCopied(false), 2000);
    }
  };

  const copyDeptLink = (deptName) => {
    const token = submission?.department_tokens?.[deptName];
    if (token) {
      const link = `${window.location.origin}/department/${token}`;
      navigator.clipboard.writeText(link);
      toast.success(`${deptName} department link copied!`);
    }
  };

  const handleSaveRenewal = async () => {
    setSavingRenewal(true);
    try {
      const res = await axios.put(`${API_URL}/submissions/${submissionId}/renewal`, {
        renewal_date: renewalDate || null,
        renewal_cycle: renewalCycle || null,
        reminder_recipients: reminderRecipients
      });
      setSubmission(res.data);
      toast.success('Renewal settings saved!');
    } catch (error) {
      toast.error('Failed to save renewal settings');
    } finally {
      setSavingRenewal(false);
    }
  };

  const handleCompleteSubmit = async () => {
    setSubmitting(true);
    try {
      await axios.put(`${API_URL}/submissions/${submissionId}`, {
        data: submission.data,
        current_section: submission.current_section || 0,
        is_completed: true,
        vendor_fields: submission.vendor_fields || [],
        field_assignments: submission.field_assignments || {}
      });
      setSubmission(prev => ({ ...prev, is_completed: true }));
      toast.success('Document completed and submitted!');
      loadData();
    } catch (error) {
      toast.error('Failed to submit document');
    } finally {
      setSubmitting(false);
    }
  };

  const handleReopenForEdit = async () => {
    setReopening(true);
    try {
      await axios.post(`${API_URL}/submissions/${submissionId}/edit`);
      toast.success('Submission reopened for editing');
      navigate(`/submissions/${submissionId}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reopen submission');
    } finally {
      setReopening(false);
    }
  };

  const addRecipient = () => {
    setReminderRecipients(prev => [...prev, { name: '', email: '' }]);
  };

  const updateRecipient = (idx, field, value) => {
    setReminderRecipients(prev => {
      const updated = [...prev];
      updated[idx] = { ...updated[idx], [field]: value };
      return updated;
    });
  };

  const removeRecipient = (idx) => {
    setReminderRecipients(prev => prev.filter((_, i) => i !== idx));
  };

  const getReminderDescription = () => {
    if (!renewalDate || !renewalCycle) return null;
    const cycleLabel = renewalCycle === '1_year' ? '1 Year' : '6 Months';
    const leadMonths = renewalCycle === '1_year' ? '11' : '5';
    return `${cycleLabel} cycle — Reminder sent at ${leadMonths} months (30 days before due date)`;
  };

  const Layout = isAdmin ? AdminLayout : ClientLayout;

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center min-h-[60vh]">
          <div className="text-center">
            <div className="w-12 h-12 border-4 border-[#FF6B35] border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
            <p className="text-slate-500">Loading preview...</p>
          </div>
        </div>
      </Layout>
    );
  }

  if (!submission || !template) return null;

  const fieldAssignments = submission.field_assignments || {};
  const deptCounts = {};
  Object.values(fieldAssignments).forEach(v => {
    if (v && v !== '__vendor__') deptCounts[v] = (deptCounts[v] || 0) + 1;
  });
  const hasDeptAssignments = Object.keys(deptCounts).length > 0;
  const deptCompleted = submission.department_completed || {};
  const versionHistory = submission.version_history || [];

  return (
    <Layout>
      <div className="page-container py-8 print:p-0">
        {/* Header - Hidden on print */}
        <div className="print:hidden">
          <PageHeader
            title="Document Preview"
            description={submission.is_completed ? "This document has been submitted." : "Review your document before submitting."}
            breadcrumbs={[
              { label: isAdmin ? 'Admin' : 'Dashboard', href: isAdmin ? '/admin' : '/dashboard' },
              { label: isAdmin ? 'Submissions' : 'My Submissions', href: isAdmin ? '/admin/submissions' : '/dashboard' },
              { label: 'Preview' }
            ]}
            actions={
              <div className="flex items-center gap-3">
                {!isAdmin && !submission.is_completed && (
                  <>
                    <Link to={`/submissions/${submissionId}`}>
                      <Button variant="outline" data-testid="edit-form-btn">
                        <Edit className="h-4 w-4 mr-2" />Edit Form
                      </Button>
                    </Link>
                    <Button
                      className="bg-emerald-600 hover:bg-emerald-700 text-white"
                      onClick={handleCompleteSubmit}
                      disabled={submitting}
                      data-testid="complete-submit-btn"
                    >
                      {submitting
                        ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Submitting...</>
                        : <><FileCheck className="h-4 w-4 mr-2" />Complete & Submit</>
                      }
                    </Button>
                  </>
                )}
                {submission.is_completed && (
                  <>
                    <span className="inline-flex items-center gap-1.5 bg-emerald-100 text-emerald-700 px-3 py-1.5 rounded-full text-sm font-medium" data-testid="completed-badge">
                      <CheckCircle2 className="h-4 w-4" /> Submitted
                    </span>
                    {!isAdmin && (
                      <Button
                        variant="outline"
                        onClick={handleReopenForEdit}
                        disabled={reopening}
                        data-testid="reopen-edit-btn"
                      >
                        {reopening
                          ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Reopening...</>
                          : <><Edit className="h-4 w-4 mr-2" />Edit</>
                        }
                      </Button>
                    )}
                  </>
                )}
                <Button variant="outline" onClick={handlePrint} data-testid="print-btn">
                  <Printer className="h-4 w-4 mr-2" />Print
                </Button>
                <Button className="btn-primary" onClick={handleDownloadPDF} data-testid="download-pdf-btn">
                  <Download className="h-4 w-4 mr-2" />Download PDF
                </Button>
              </div>
            }
          />

          {/* Version Info */}
          {submission.version && (
            <div className="bg-slate-50 border border-slate-200 rounded-xl p-4 mb-4" data-testid="version-section">
              <div className="flex items-center justify-between flex-wrap gap-4">
                <div className="flex items-center gap-3">
                  <Tag className="h-5 w-5 text-slate-500" />
                  <div>
                    <p className="font-medium text-slate-900">
                      Version: <span className="font-mono text-[#FF6B35]">{submission.version}</span>
                    </p>
                    {versionHistory.length > 0 && (
                      <p className="text-sm text-slate-500">
                        {versionHistory.length} version event{versionHistory.length !== 1 ? 's' : ''} recorded
                      </p>
                    )}
                  </div>
                </div>
                {versionHistory.length > 1 && (
                  <details className="text-sm">
                    <summary className="cursor-pointer text-slate-500 hover:text-[#FF6B35] flex items-center gap-1">
                      <History className="h-4 w-4" /> View History
                    </summary>
                    <div className="mt-3 space-y-2 border-t border-slate-200 pt-3">
                      {[...versionHistory].reverse().map((entry, idx) => (
                        <div key={idx} className="flex items-center gap-3 text-sm" data-testid={`version-entry-${idx}`}>
                          <span className={`w-2 h-2 rounded-full flex-shrink-0 ${
                            entry.action === 'Submitted' || entry.action === 'Resubmitted' 
                              ? 'bg-emerald-500' 
                              : entry.action === 'Reopened for editing'
                                ? 'bg-amber-500'
                                : 'bg-slate-400'
                          }`}></span>
                          <span className="font-mono text-slate-600">{entry.version}</span>
                          <span className="text-slate-500">{entry.action}</span>
                          <span className="text-slate-400">{new Date(entry.date).toLocaleDateString()} {new Date(entry.date).toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})}</span>
                        </div>
                      ))}
                    </div>
                  </details>
                )}
              </div>
            </div>
          )}

          {/* Audit Log */}
          {auditLog.length > 0 && (
            <div className="bg-slate-50 border border-slate-200 rounded-xl p-4 mb-4" data-testid="audit-log-section">
              <button
                onClick={() => setAuditExpanded(!auditExpanded)}
                className="flex items-center justify-between w-full text-left"
              >
                <div className="flex items-center gap-3">
                  <ClipboardList className="h-5 w-5 text-slate-500" />
                  <div>
                    <p className="font-medium text-slate-900">Field Audit Log</p>
                    <p className="text-sm text-slate-500">{auditLog.length} field change{auditLog.length !== 1 ? 's' : ''} tracked</p>
                  </div>
                </div>
                <span className="text-sm text-slate-400">{auditExpanded ? 'Collapse' : 'Expand'}</span>
              </button>
              {auditExpanded && (
                <div className="mt-4 max-h-80 overflow-y-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="text-xs text-slate-500 uppercase tracking-wider border-b border-slate-200">
                        <th className="text-left py-2 pr-2">Field</th>
                        <th className="text-left py-2 pr-2">Changed By</th>
                        <th className="text-left py-2 pr-2">Type</th>
                        <th className="text-left py-2">When</th>
                      </tr>
                    </thead>
                    <tbody>
                      {auditLog.map((entry, idx) => (
                        <tr key={idx} className="border-b border-slate-100">
                          <td className="py-2 pr-2 font-mono text-xs text-slate-700">{entry.field_name}</td>
                          <td className="py-2 pr-2 text-slate-600">{entry.changed_by_name}</td>
                          <td className="py-2 pr-2">
                            <span className={`px-1.5 py-0.5 rounded text-xs font-medium ${
                              entry.changed_by_type === 'client' ? 'bg-blue-100 text-blue-700' :
                              entry.changed_by_type === 'vendor' ? 'bg-purple-100 text-purple-700' :
                              'bg-teal-100 text-teal-700'
                            }`}>
                              {entry.changed_by_type}
                            </span>
                          </td>
                          <td className="py-2 text-slate-400 text-xs">{new Date(entry.timestamp).toLocaleString()}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}

          {/* Vendor Actions */}
          {!isAdmin && submission.vendor_fields?.length > 0 && (
            <div className="bg-purple-50 border border-purple-200 rounded-xl p-4 mb-4">
              <div className="flex items-center justify-between flex-wrap gap-4">
                <div className="flex items-center gap-3">
                  <UserCog className="h-5 w-5 text-purple-600" />
                  <div>
                    <p className="font-medium text-purple-900">
                      {submission.vendor_fields.length} field(s) assigned to vendor
                    </p>
                    <p className="text-sm text-purple-700">
                      {submission.vendor_completed 
                        ? <span className="flex items-center gap-1"><CheckCircle2 className="h-4 w-4 text-emerald-600" /> Vendor has completed their sections</span>
                        : 'Send a link to your vendor to complete their sections'}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <Button variant="outline" size="sm" onClick={copyVendorLink} data-testid="copy-vendor-link-btn">
                    {vendorLinkCopied ? <CheckCircle2 className="h-4 w-4 mr-1" /> : <Copy className="h-4 w-4 mr-1" />}
                    {vendorLinkCopied ? 'Copied!' : 'Copy Link'}
                  </Button>
                  <Button size="sm" className="bg-purple-600 hover:bg-purple-700 text-white" onClick={handleSendToVendor} disabled={sendingToVendor} data-testid="send-to-vendor-btn">
                    <Send className="h-4 w-4 mr-1" />
                    {sendingToVendor ? 'Sending...' : 'Email to Vendor'}
                  </Button>
                </div>
              </div>
            </div>
          )}

          {/* Department Actions */}
          {!isAdmin && hasDeptAssignments && (
            <div className="bg-teal-50 border border-teal-200 rounded-xl p-4 mb-4" data-testid="dept-actions-section">
              <div className="flex items-center justify-between flex-wrap gap-4">
                <div className="flex items-center gap-3">
                  <Layers className="h-5 w-5 text-teal-600" />
                  <div>
                    <p className="font-medium text-teal-900">
                      {Object.keys(deptCounts).length} department(s) with assigned fields
                    </p>
                    <div className="flex flex-wrap gap-2 mt-1">
                      {Object.entries(deptCounts).map(([dept, count]) => (
                        <span key={dept} className="inline-flex items-center gap-1 text-xs">
                          <span className={`px-2 py-0.5 rounded-full font-medium ${deptCompleted[dept] ? 'bg-emerald-100 text-emerald-700' : 'bg-teal-100 text-teal-800'}`}>
                            {dept}: {count} field{count !== 1 ? 's' : ''}
                            {deptCompleted[dept] && <CheckCircle2 className="inline h-3 w-3 ml-1" />}
                          </span>
                          <button onClick={() => copyDeptLink(dept)} className="text-teal-500 hover:text-teal-700" title={`Copy ${dept} link`}>
                            <Copy className="h-3 w-3" />
                          </button>
                        </span>
                      ))}
                    </div>
                  </div>
                </div>
                <Button size="sm" className="bg-teal-600 hover:bg-teal-700 text-white" onClick={handleSendToDepts} disabled={sendingToDepts} data-testid="send-to-depts-btn">
                  {sendingToDepts ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Send className="h-4 w-4 mr-1" />}
                  {sendingToDepts ? 'Sending...' : 'Notify Departments'}
                </Button>
              </div>
            </div>
          )}

          {/* Renewal & Reminder Settings */}
          {!isAdmin && (
            <div className="bg-white border border-slate-200 rounded-xl p-5 mb-6" data-testid="renewal-section">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 bg-amber-50 rounded-lg flex items-center justify-center">
                  <Calendar className="h-5 w-5 text-amber-600" />
                </div>
                <div>
                  <h3 className="text-base font-semibold text-slate-900" style={{ fontFamily: 'Outfit, sans-serif' }}>Industry Renewal & Reminders</h3>
                  <p className="text-sm text-slate-500">Set the compliance due date and renewal cycle</p>
                </div>
              </div>

              <div className="grid md:grid-cols-2 gap-4 mb-4">
                <div className="space-y-1.5">
                  <Label>Industry Due Date</Label>
                  <Input
                    type="date"
                    value={renewalDate}
                    onChange={e => setRenewalDate(e.target.value)}
                    data-testid="renewal-date-input"
                  />
                </div>
                <div className="space-y-1.5">
                  <Label>Renewal Cycle</Label>
                  <Select value={renewalCycle} onValueChange={setRenewalCycle}>
                    <SelectTrigger data-testid="renewal-cycle-select">
                      <SelectValue placeholder="Select renewal cycle" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="1_year">1 Year (reminder at 11 months)</SelectItem>
                      <SelectItem value="6_month">6 Months (reminder at 5 months)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* Cycle description */}
              {getReminderDescription() && (
                <div className="bg-blue-50 border border-blue-100 rounded-lg p-3 mb-4 text-sm text-blue-700 flex items-center gap-2" data-testid="cycle-description">
                  <RefreshCw className="h-4 w-4 flex-shrink-0" />
                  {getReminderDescription()}
                </div>
              )}

              {/* Reminder Recipients */}
              <div className="mb-4">
                <Label className="flex items-center gap-2 mb-2">
                  <Bell className="h-4 w-4 text-amber-500" />
                  Reminder Recipients
                </Label>
                {reminderRecipients.length === 0 && (
                  <p className="text-sm text-slate-400 mb-2">No recipients added. Add people who should receive the renewal reminder.</p>
                )}
                <div className="space-y-2">
                  {reminderRecipients.map((r, idx) => (
                    <div key={idx} className="flex items-center gap-2" data-testid={`recipient-${idx}`}>
                      <Input
                        placeholder="Name"
                        value={r.name}
                        onChange={e => updateRecipient(idx, 'name', e.target.value)}
                        className="flex-1"
                        data-testid={`recipient-name-${idx}`}
                      />
                      <Input
                        placeholder="email@company.com"
                        type="email"
                        value={r.email}
                        onChange={e => updateRecipient(idx, 'email', e.target.value)}
                        className="flex-1"
                        data-testid={`recipient-email-${idx}`}
                      />
                      <Button type="button" variant="ghost" size="sm" className="text-red-500 hover:text-red-700 h-9 w-9 p-0 flex-shrink-0" onClick={() => removeRecipient(idx)} data-testid={`remove-recipient-${idx}`}>
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  ))}
                </div>
                <Button type="button" variant="outline" size="sm" onClick={addRecipient} className="mt-2 gap-1.5" data-testid="add-recipient-btn">
                  <Plus className="h-3.5 w-3.5" /> Add Recipient
                </Button>
              </div>

              {/* Renewal status & save */}
              <div className="flex items-center justify-between pt-3 border-t border-slate-100">
                <div className="text-sm text-slate-500 flex items-center gap-2">
                  {submission.reminder_sent ? (
                    <span className="flex items-center gap-1 text-emerald-600">
                      <CheckCircle2 className="h-4 w-4" /> Reminder sent
                    </span>
                  ) : renewalDate && renewalCycle ? (
                    <span className="flex items-center gap-1 text-amber-600">
                      <Clock className="h-4 w-4" /> Due: {new Date(renewalDate + 'T00:00:00').toLocaleDateString()}
                    </span>
                  ) : (
                    <span className="text-slate-400">No renewal configured</span>
                  )}
                </div>
                <Button onClick={handleSaveRenewal} disabled={savingRenewal} size="sm" className="btn-primary gap-1.5" data-testid="save-renewal-btn">
                  {savingRenewal ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Calendar className="h-3.5 w-3.5" />}
                  {savingRenewal ? 'Saving...' : 'Save Renewal Settings'}
                </Button>
              </div>
            </div>
          )}
        </div>

        {/* Compliance Check Section - Print Hidden */}
        <ComplianceCheckSection
          submissionId={submissionId}
          isAdmin={isAdmin}
          submission={submission}
          template={template}
        />

        {/* PDF Preview */}
        <div ref={printRef} className="pdf-preview print:shadow-none print:rounded-none">
          {/* PDF Header */}
          <div className="pdf-header print:bg-[#FF6B35]">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <img src={LOGO_URL} alt="AuditGuard" className="h-16 w-auto rounded-lg bg-white p-1" />
                <div>
                  <h1 className="text-2xl font-bold" style={{ fontFamily: 'Outfit, sans-serif' }}>
                    {template.name}
                  </h1>
                  <p className="text-white/80">AuditGuard</p>
                </div>
              </div>
              <div className="text-right text-sm text-white/80">
                <p>Document ID: <span className="font-mono">{submission.id.slice(0, 8)}</span></p>
                {submission.version && <p>Version: <span className="font-mono">{submission.version}</span></p>}
                <p>Generated: {new Date().toLocaleDateString()}</p>
                {renewalDate && <p>Next Renewal: {new Date(renewalDate + 'T00:00:00').toLocaleDateString()}</p>}
              </div>
            </div>
          </div>

          {/* PDF Body */}
          <div className="pdf-body">
            {/* Document Info */}
            <div className="grid grid-cols-2 gap-6 mb-8 p-4 bg-slate-50 rounded-lg">
              <div className="flex items-center gap-3">
                <Building2 className="h-5 w-5 text-slate-400" />
                <div>
                  <p className="text-xs text-slate-500 uppercase tracking-wider">Company</p>
                  <p className="font-medium text-slate-900">{submission.company_name || 'N/A'}</p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <Calendar className="h-5 w-5 text-slate-400" />
                <div>
                  <p className="text-xs text-slate-500 uppercase tracking-wider">Completed</p>
                  <p className="font-medium text-slate-900">{new Date(submission.updated_at).toLocaleDateString()}</p>
                </div>
              </div>
            </div>

            {/* Sections */}
            {template.sections.map((section, sectionIndex) => (
              <div key={section.id} className="pdf-section">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-8 h-8 bg-[#FF6B35] text-white rounded-lg flex items-center justify-center text-sm font-bold">
                    {sectionIndex + 1}
                  </div>
                  <h2 className="text-xl font-semibold text-slate-900" style={{ fontFamily: 'Outfit, sans-serif' }}>
                    {section.title}
                  </h2>
                </div>

                <div className="grid gap-4 pl-11">
                  {section.fields.map((field) => {
                    const value = submission.data?.[field.name];
                    const assignee = fieldAssignments[field.name] || '';
                    const isVendorField = assignee === '__vendor__';
                    const isDeptField = assignee && assignee !== '__vendor__';
                    const isAssigned = isVendorField || isDeptField;
                    // Skip N/A fields
                    if (typeof value === 'string' && ['n/a', 'na'].includes(value.trim().toLowerCase())) return null;
                    if (!value && !field.required && !isAssigned) return null;
                    
                    return (
                      <div key={field.name} className={`pdf-field ${
                        isVendorField ? 'bg-purple-50 p-3 rounded-lg border border-purple-100' :
                        isDeptField ? 'bg-teal-50 p-3 rounded-lg border border-teal-100' : ''
                      }`}>
                        <div className="pdf-field-label flex items-center gap-2">
                          {field.label}
                          {isVendorField && (
                            <span className="inline-flex items-center gap-1 bg-purple-100 text-purple-700 px-2 py-0.5 rounded-full text-xs font-medium">
                              <UserCog className="h-3 w-3" />Vendor Assigned
                            </span>
                          )}
                          {isDeptField && (
                            <span className="inline-flex items-center gap-1 bg-teal-100 text-teal-700 px-2 py-0.5 rounded-full text-xs font-medium">
                              <Layers className="h-3 w-3" />{assignee}
                            </span>
                          )}
                        </div>
                        <div className="pdf-field-value whitespace-pre-wrap">
                          {value || <span className="text-slate-400 italic">
                            {isVendorField ? 'Pending vendor input' : isDeptField ? `Pending ${assignee} input` : 'Not provided'}
                          </span>}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}

            {/* Footer */}
            <div className="mt-12 pt-8 border-t border-slate-200 text-center text-sm text-slate-500">
              <p>This document was generated with AuditGuard Client Portal</p>
              <p className="mt-1">
                <a href="https://www.audit-guard.net" className="text-[#FF6B35] hover:underline">
                  www.audit-guard.net
                </a>
              </p>
              <p className="mt-1 text-slate-400">
                Need to get set up? <a href="https://www.audit-guard.net/contact" className="text-[#FF6B35] hover:underline">Contact Us</a>
              </p>
            </div>
          </div>
        </div>

        {/* Back Link - Hidden on print */}
        <div className="mt-8 text-center print:hidden">
          <Link 
            to={isAdmin ? '/admin/submissions' : '/dashboard'} 
            className="text-slate-500 hover:text-[#FF6B35] transition-colors inline-flex items-center gap-2"
          >
            <ArrowLeft className="h-4 w-4" />
            Back to {isAdmin ? 'Submissions' : 'Dashboard'}
          </Link>
        </div>
      </div>

      {/* Print Styles */}
      <style>{`
        @media print {
          body * {
            visibility: hidden;
          }
          .pdf-preview, .pdf-preview * {
            visibility: visible;
          }
          .pdf-preview {
            position: absolute;
            left: 0;
            top: 0;
            width: 100%;
          }
          .print\\:hidden {
            display: none !important;
          }
        }
      `}</style>
    </Layout>
  );
}


function ComplianceCheckSection({ submissionId, isAdmin, submission, template }) {
  const navigate = useNavigate();
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(true);
  const [running, setRunning] = useState(false);
  const [runMode, setRunMode] = useState(null);
  const [collapsed, setCollapsed] = useState(false);

  useEffect(() => {
    loadCheck();
  }, [submissionId]);

  const loadCheck = async () => {
    try {
      const endpoint = isAdmin
        ? `${API_URL}/admin/submissions/${submissionId}/compliance-check`
        : `${API_URL}/submissions/${submissionId}/compliance-check`;
      const res = await axios.get(endpoint);
      setResult(res.data);
    } catch { /* no existing check */ }
    finally { setLoading(false); }
  };

  const runCheck = async (mode) => {
    setRunning(true);
    setRunMode(mode);
    try {
      const endpoint = isAdmin
        ? `${API_URL}/admin/submissions/${submissionId}/compliance-check`
        : `${API_URL}/submissions/${submissionId}/compliance-check`;
      const res = await axios.post(endpoint, { mode });
      setResult(res.data);
      toast.success(mode === 'ai' ? 'AI compliance check complete' : 'Completeness check complete');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Compliance check failed');
    } finally {
      setRunning(false);
      setRunMode(null);
    }
  };

  const getScoreColor = (score) => {
    if (score >= 80) return 'text-emerald-600';
    if (score >= 50) return 'text-amber-600';
    return 'text-red-600';
  };

  const getScoreBg = (score) => {
    if (score >= 80) return 'bg-emerald-50 border-emerald-200';
    if (score >= 50) return 'bg-amber-50 border-amber-200';
    return 'bg-red-50 border-red-200';
  };

  const getStatusIcon = (status) => {
    if (status === 'compliant') return <CheckCircle2 className="h-4 w-4 text-emerald-500" />;
    if (status === 'partial') return <AlertTriangle className="h-4 w-4 text-amber-500" />;
    return <XCircle className="h-4 w-4 text-red-500" />;
  };

  if (loading) return null;

  return (
    <div className="bg-white rounded-xl border border-slate-200 overflow-hidden print:hidden mb-8" data-testid="compliance-check-section">
      <button
        onClick={() => setCollapsed(!collapsed)}
        className="w-full p-6 border-b border-slate-100 text-left hover:bg-slate-50/50 transition-colors"
        data-testid="compliance-section-toggle"
      >
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-indigo-50 rounded-lg flex items-center justify-center">
              <ShieldCheck className="h-5 w-5 text-indigo-600" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-slate-900" style={{ fontFamily: 'Outfit, sans-serif' }}>
                Compliance Check
              </h2>
              <p className="text-sm text-slate-500">Verify ISO compliance and get a quality score</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            {/* Score badge */}
            {result && (
              <div className={`px-4 py-2 rounded-xl border ${getScoreBg(result.score)}`} data-testid="compliance-score-badge">
                <p className="text-xs text-slate-500 uppercase tracking-wider">Score</p>
                <p className={`text-3xl font-bold ${getScoreColor(result.score)}`}>{result.score}<span className="text-base font-normal text-slate-400">/100</span></p>
              </div>
            )}
            <span className="text-sm text-slate-400">{collapsed ? 'Expand' : 'Collapse'}</span>
          </div>
        </div>
      </button>

      {!collapsed && (
      <div className="p-6">
        {/* Action Buttons */}
        <div className="flex flex-wrap gap-3 mb-6">
          <Button
            onClick={() => runCheck('ai')}
            disabled={running}
            className="bg-indigo-600 hover:bg-indigo-700 text-white gap-2"
            data-testid="run-ai-check-btn"
          >
            {running && runMode === 'ai' ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />}
            {running && runMode === 'ai' ? 'AI Analyzing...' : 'AI Compliance Review'}
          </Button>
          <Button
            onClick={() => runCheck('manual')}
            disabled={running}
            variant="outline"
            className="gap-2"
            data-testid="run-manual-check-btn"
          >
            {running && runMode === 'manual' ? <Loader2 className="h-4 w-4 animate-spin" /> : <Search className="h-4 w-4" />}
            {running && runMode === 'manual' ? 'Checking...' : 'Completeness Check'}
          </Button>
        </div>

        {result ? (
          <div className="space-y-6">
            {/* Summary */}
            <div className="bg-slate-50 rounded-lg p-4" data-testid="compliance-summary">
              <p className="text-sm text-slate-700">{result.summary}</p>
              <p className="text-xs text-slate-400 mt-2">
                Checked by: {result.checked_by} | {new Date(result.checked_at).toLocaleString()}
              </p>
            </div>

            {/* Section Completeness with Fix Me */}
            {result.section_completeness && template?.sections && (() => {
              const incompleteSections = template.sections
                .map((s, idx) => ({ ...s, idx, stats: result.section_completeness[s.id] }))
                .filter(s => s.stats && s.stats.completion_pct < 100 && s.stats.missing_fields?.length > 0);
              if (incompleteSections.length === 0) return null;
              return (
                <div data-testid="section-completeness">
                  <h3 className="text-sm font-semibold text-slate-700 mb-3 flex items-center gap-2">
                    <Wrench className="h-4 w-4" /> Sections Needing Attention ({incompleteSections.length})
                  </h3>
                  <div className="space-y-2">
                    {incompleteSections.map(s => (
                      <div key={s.id} className="flex items-center gap-3 p-3 bg-white rounded-lg border border-slate-100" data-testid={`section-fix-${s.idx}`}>
                        <div className="flex-shrink-0">
                          <div className="w-10 h-10 rounded-full flex items-center justify-center text-xs font-bold border-2" style={{
                            borderColor: s.stats.completion_pct >= 80 ? '#10b981' : s.stats.completion_pct >= 50 ? '#f59e0b' : '#ef4444',
                            color: s.stats.completion_pct >= 80 ? '#10b981' : s.stats.completion_pct >= 50 ? '#f59e0b' : '#ef4444',
                          }}>
                            {s.stats.completion_pct}%
                          </div>
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-slate-800">{s.stats.title}</p>
                          <p className="text-xs text-slate-500 mt-0.5">
                            {s.stats.missing_fields.length} empty field{s.stats.missing_fields.length !== 1 ? 's' : ''}: {s.stats.missing_fields.slice(0, 3).map(f => f.label).join(', ')}{s.stats.missing_fields.length > 3 ? ` +${s.stats.missing_fields.length - 3} more` : ''}
                          </p>
                        </div>
                        <Button
                          size="sm"
                          onClick={() => navigate(`/submissions/${submissionId}?section=${s.idx}`)}
                          className="bg-[#FF6B35] hover:bg-[#e55a2b] text-white gap-1.5 flex-shrink-0"
                          data-testid={`fix-me-btn-${s.idx}`}
                        >
                          <Wrench className="h-3.5 w-3.5" /> Fix Me
                        </Button>
                      </div>
                    ))}
                  </div>
                </div>
              );
            })()}

            {/* Clause Results */}
            {result.clause_results?.length > 0 && (
              <div>
                <h3 className="text-sm font-semibold text-slate-700 mb-3 flex items-center gap-2">
                  <ClipboardList className="h-4 w-4" /> ISO Clause Assessment ({result.iso_standard})
                </h3>
                <div className="space-y-2">
                  {result.clause_results.map((clause, i) => (
                    <div key={i} className="flex items-start gap-3 p-3 bg-white rounded-lg border border-slate-100" data-testid={`clause-result-${i}`}>
                      {getStatusIcon(clause.status)}
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-slate-800">{clause.clause}</p>
                        <p className="text-xs text-slate-500 mt-0.5">{clause.finding}</p>
                      </div>
                      <span className={`text-xs font-medium px-2 py-0.5 rounded-full capitalize ${
                        clause.status === 'compliant' ? 'bg-emerald-100 text-emerald-700' :
                        clause.status === 'partial' ? 'bg-amber-100 text-amber-700' :
                        'bg-red-100 text-red-700'
                      }`}>{clause.status}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Strengths & Gaps */}
            <div className="grid md:grid-cols-2 gap-4">
              {result.strengths?.length > 0 && (
                <div className="bg-emerald-50 rounded-lg p-4 border border-emerald-100">
                  <h4 className="text-sm font-semibold text-emerald-800 mb-2 flex items-center gap-1.5">
                    <TrendingUp className="h-4 w-4" /> Strengths
                  </h4>
                  <ul className="space-y-1">
                    {result.strengths.map((s, i) => (
                      <li key={i} className="text-xs text-emerald-700 flex items-start gap-1.5">
                        <CheckCircle2 className="h-3 w-3 mt-0.5 flex-shrink-0" /> {s}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              {result.gaps?.length > 0 && (
                <div className="bg-red-50 rounded-lg p-4 border border-red-100">
                  <h4 className="text-sm font-semibold text-red-800 mb-2 flex items-center gap-1.5">
                    <AlertTriangle className="h-4 w-4" /> Gaps
                  </h4>
                  <ul className="space-y-1">
                    {result.gaps.map((g, i) => (
                      <li key={i} className="text-xs text-red-700 flex items-start gap-1.5">
                        <XCircle className="h-3 w-3 mt-0.5 flex-shrink-0" /> {g}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>

            {/* Recommendations */}
            {result.recommendations?.length > 0 && (
              <div className="bg-blue-50 rounded-lg p-4 border border-blue-100">
                <h4 className="text-sm font-semibold text-blue-800 mb-2 flex items-center gap-1.5">
                  <Sparkles className="h-4 w-4" /> Recommendations
                </h4>
                <ul className="space-y-1.5">
                  {result.recommendations.map((r, i) => (
                    <li key={i} className="text-xs text-blue-700">{i + 1}. {r}</li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        ) : (
          <div className="text-center py-8 text-slate-400">
            <ShieldCheck className="h-12 w-12 mx-auto mb-3 opacity-30" />
            <p className="text-sm">No compliance check has been run yet.</p>
            <p className="text-xs mt-1">Run an AI review or completeness check to get your compliance score.</p>
          </div>
        )}
      </div>
      )}
    </div>
  );
}
